/**
 * Access Platform (v3 ZTNA) API service.
 *
 * Talks to /api/v2/access/*. Two conventions worth knowing:
 *
 * 1. UNWRAPPING. The axios response interceptor in api.ts already strips the
 *    axios layer and returns the HTTP body, which is the v2 envelope
 *    `{ success, data, timestamp }`. So every call here reads `.data` once
 *    more. (mtge.service.ts casts instead of unwrapping and silently yields
 *    undefined at runtime — do not copy that.)
 *
 * 2. TENANT SCOPING. Tenant-scoped Access endpoints require SuperAdmins to name
 *    a tenant explicitly; there is deliberately no server-side default (the
 *    legacy default-tenant fallback is what misfiled devices in VSNP-846). The
 *    api.ts interceptor only injects `tenantId` on GETs, so `tenantParams()`
 *    below adds it to writes too.
 */

import api from './api';
import { useAuthStore } from '../stores/authStore';
import { useTenantStore } from '../stores/tenantStore';
import { USER_TYPES } from '../utils/constants';

const BASE = '/api/v2/access';

/** The v2 API success envelope. */
interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
  timestamp: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Explicit tenant scoping for SuperAdmins.
 * Tenant admins are pinned server-side, so nothing is sent for them.
 */
function tenantParams(): Record<string, string> {
  const { user } = useAuthStore.getState();
  if (user?.type !== USER_TYPES.SUPER_ADMIN) return {};

  const tenantId = useTenantStore.getState().getTenantId();
  return tenantId ? { tenantId } : {};
}

function unwrap<T>(response: unknown): T {
  return (response as ApiEnvelope<T>).data;
}

// ============================================
// Types
// ============================================

export type AccessNodeStatus =
  | 'pending_approval'
  | 'approved'
  | 'provisioned'
  | 'active'
  | 'degraded'
  | 'offline'
  | 'revoked'
  | 'decommissioned';

export type AccessHealthState = 'unknown' | 'up' | 'degraded' | 'down';
export type ReleaseChannel = 'stable' | 'canary' | 'beta';
/**
 * `external` is a SaaS/internet-breakout app: it has no connector target, and is
 * matched by the hostname the client announces (TLS SNI / HTTP Host) against its
 * `fqdn_patterns`. Everything else routes to a connector group.
 */
export type AccessAppType = 'http' | 'https' | 'tcp' | 'udp' | 'subnet' | 'external';

/**
 * Node-reported hardware/OS facts pushed by the bootstrap at enrolment
 * (design §10.2). Read-only in the UI — the machine knows these better than
 * the admin.
 */
export interface NodeDeviceSpec {
  hostname?: string;
  os?: string;
  osVersion?: string;
  kernel?: string;
  arch?: string;
  cpuCores?: number;
  memoryMb?: number;
  diskGb?: number;
  virtualization?: string;
  clientVersion?: string;
  nics?: { name: string; mac?: string; ipv4?: string[] }[];
}

/** The license decision, fixed at creation (D4) — repurpose = factory reset + new token. */
export type GatewaySharingMode = 'dedicated' | 'shared';

export interface GatewayTenantAssignment {
  id: string;
  gateway_id: string;
  tenant_id: string;
  tenant_slot: number;
  isolation_id: number;
  pool_cidr: string | null;
  status: 'assigning' | 'active' | 'suspended' | 'removing';
  created_at: string;
}

export interface AccessGateway {
  id: string;
  node_id: string;
  sharing_mode: GatewaySharingMode;
  owner_tenant_id: string | null;
  max_tenants: number;
  current_tenants: number;
  name: string | null;
  pop_code: string | null;
  region: string | null;
  fqdn: string | null;
  public_endpoint: string | null;
  public_ip: string | null;
  quic_port: number;
  max_sessions: number;
  version: string | null;
  desired_version: string | null;
  release_channel: ReleaseChannel;
  rollout_ring: number;
  certificate_serial_number: string | null;
  certificate_issued_at: string | null;
  certificate_expires_at: string | null;
  device_spec: NodeDeviceSpec | null;
  status: AccessNodeStatus;
  registered_at: string;
  last_seen: string | null;
  config_epoch: number;
  last_health: AccessHealthState;
  /**
   * Per-component health from the last status heartbeat — the list
   * `last_health` is rolled up from. The metrics plane may report MORE
   * components (agw_component_up); see GatewayDetail (VSNP-1438).
   */
  last_components?: NodeComponentHealth[] | null;
  last_session_count: number | null;
  last_flow_count: number | null;
  last_status_at: string | null;
  /**
   * Log level (VSNP-1338). `log_level` is the orchestrator's INTENT (NULL =
   * unset, which the node treats as "leave it alone", i.e. the node default,
   * info). `effective_log_level` is what the node REPORTS its logger is
   * running at; NULL until a build that reports it does.
   * `log_level_override` is derived by the API: true when the two disagree,
   * null when the effective level is unknown.
   */
  log_level?: string | null;
  effective_log_level?: string | null;
  effective_log_level_since?: string | null;
  log_level_override?: boolean | null;
  created_at: string;
}

export interface UpdateGatewayData {
  name?: string;
  popCode?: string | null;
  region?: string | null;
  fqdn?: string | null;
  publicEndpoint?: string | null;
  quicPort?: number;
  maxSessions?: number;
  desiredVersion?: string | null;
  releaseChannel?: ReleaseChannel;
  rolloutRing?: number;
}

export interface CreateGatewayData {
  name: string;
  /**
   * The node's stable identity and certificate CN. The wizard derives it from
   * the name — if omitted the API generates an opaque UUID, which the operator
   * would then have to configure as the bootstrap's node id without ever
   * seeing it (the CSR CN must match the registered node_id at enrolment).
   */
  nodeId?: string;
  popCode?: string;
  region?: string;
  fqdn?: string;
  publicEndpoint?: string;
  quicPort?: number;
  maxSessions?: number;
  releaseChannel?: ReleaseChannel;
  rolloutRing?: number;
  /** The license decision (D4: immutable). dedicated requires ownerTenantId; shared takes maxTenants. */
  sharingMode?: GatewaySharingMode;
  ownerTenantId?: string;
  maxTenants?: number;
}

export interface GatewayFleetSummary {
  total: number;
  byStatus: Record<string, number>;
  byVersion: Record<string, number>;
}

/**
 * One entry of a node's per-component health, as reported on its heartbeat
 * (accessv1.ComponentHealth). Stored verbatim from the last heartbeat.
 */
export interface NodeComponentHealth {
  name: string;
  state: AccessHealthState;
  detail?: string;
}

export interface AppConnector {
  id: string;
  tenant_id: string;
  node_id: string;
  name: string | null;
  connector_group: string;
  description: string | null;
  deployment_type: 'vm' | 'container' | 'kubernetes';
  site: string | null;
  version: string | null;
  release_channel: ReleaseChannel;
  certificate_serial_number: string | null;
  certificate_issued_at: string | null;
  certificate_expires_at: string | null;
  device_spec: NodeDeviceSpec | null;
  status: AccessNodeStatus;
  health: AccessHealthState;
  /**
   * Per-component health from the last heartbeat (V37) — the list `health` is
   * rolled up from. NULL until the connector heartbeats against an API that
   * stores it.
   */
  last_components?: NodeComponentHealth[] | null;
  registered_at: string;
  last_seen: string | null;
  created_at: string;
}

export interface CreateConnectorData {
  name: string;
  /** See CreateGatewayData.nodeId — the CN the enrolment CSR must carry. */
  nodeId?: string;
  connectorGroup?: string;
  description?: string;
  deploymentType?: 'vm' | 'container' | 'kubernetes';
  site?: string;
  releaseChannel?: ReleaseChannel;
}

export interface UpdateConnectorData {
  name?: string;
  connectorGroup?: string;
  description?: string | null;
  site?: string | null;
  desiredVersion?: string | null;
  releaseChannel?: ReleaseChannel;
}

// ---------- Provisioning claims (registration tokens) ----------

/**
 * One row from GET /api/v2/provision/claims. The token is MASKED here
 * (first/last groups only) — the full token exists client-side exactly once,
 * in the MintedClaim returned by the create call. That is the display-once
 * guarantee, enforced server-side, that the wizard's step 2 leans on.
 */
export interface ProvisionClaim {
  id: string;
  token: string;
  tenant_id: string;
  device_type: 'edge' | 'mtge' | 'connector' | 'gateway' | 'app_connector';
  device_id: string | null;
  edge_name: string | null;
  status: 'active' | 'claimed' | 'expired' | 'revoked';
  expires_at: string;
  claimed_at: string | null;
  claimed_by_guid: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
}

/** The create response — the only place the FULL token ever appears. */
export interface MintedClaim {
  id: string;
  token: string;
  deviceType: string;
  tenantId: string;
  deviceId: string | null;
  status: string;
  expiresAt: string;
  createdAt: string;
}

export interface ConnectorGroupSummary {
  connectorGroup: string;
  total: number;
  healthy: number;
  /** Set for REGISTERED groups (VSNP-1097); a purely-derived group has none. */
  id?: string;
  description?: string | null;
  registered?: boolean;
}

/** GET /access/connectors/groups/:name — powers the per-group management page. */
export interface ConnectorGroupDetail extends ConnectorGroupSummary {
  /** Connectors currently assigned to this group. */
  members: AppConnector[];
  /** Enabled access apps that target this group by name. */
  appCount: number;
}

export interface AccessApp {
  id: string;
  tenant_id: string;
  name: string;
  display_name: string | null;
  description: string | null;
  app_type: AccessAppType;
  target_host: string | null;
  target_port: number | null;
  target_cidr: string | null;
  connector_group: string;
  public_hostname: string | null;
  /** External (SaaS) apps only: hostname / "*." suffix patterns matched by SNI. */
  fqdn_patterns: string[] | null;
  l7_options: Record<string, unknown> | null;
  tags: string[] | null;
  enabled: number;
  created_at: string;
  /**
   * The URL the client's Open button uses, derived by the API (VSNP-1938, doc 28f §4); null when the
   * client shows "Copy address" instead. Absent from an API older than p-1640a18.
   */
  launchUrl?: string | null;
}

/** A member app inside an Application Group (compact projection). */
export interface AppGroupMemberApp {
  id: string;
  name: string;
  appType: AccessAppType;
  enabled: boolean;
}

/** A named Application Group — the WHAT-side peer of an identity group (V28). */
export interface AppGroup {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  members: AppGroupMemberApp[];
  memberCount: number;
  created_at: string;
}

export interface AppGroupData {
  name: string;
  description?: string;
  /** Full member set (app ids); replaces the existing members on update. */
  appIds?: string[];
}

/**
 * A named Device Posture Profile (V31) — a reusable requirement set a policy
 * references by id. The compiler resolves it into the rule's posture clauses.
 */
export interface PostureProfile {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  requirements: Record<string, PosturePredicate>;
  /** Policies currently referencing this profile (delete is refused while > 0). */
  ruleCount: number;
  ruleNames: string[];
  created_at: string;
  updated_at: string | null;
}

export interface PostureProfileData {
  name: string;
  description?: string;
  requirements: Record<string, PosturePredicate>;
}

export interface CreateAppData {
  name: string;
  displayName?: string;
  description?: string;
  appType: AccessAppType;
  targetHost?: string;
  targetPort?: number;
  targetCidr?: string;
  connectorGroup?: string;
  publicHostname?: string;
  /** Required (>=1) for external apps; ignored otherwise. */
  fqdnPatterns?: string[];
  l7Options?: Record<string, unknown>;
  tags?: string[];
  /** For a SaaS app: the catalog profile id it was created from (VSNP-1150). */
  saasCatalogId?: string;
  enabled?: boolean;
}

export interface UpdateAppData {
  displayName?: string;
  description?: string | null;
  appType?: AccessAppType;
  targetHost?: string;
  targetPort?: number;
  targetCidr?: string;
  connectorGroup?: string;
  publicHostname?: string;
  fqdnPatterns?: string[];
  l7Options?: Record<string, unknown>;
  tags?: string[];
  enabled?: boolean;
}

/**
 * A SaaS catalog profile (VSNP-1150): a SuperAdmin-curated, global entry that
 * tenant admins pick from when creating a SaaS app instead of typing domains.
 * `appCount` is a live count of apps referencing it — a profile with appCount>0
 * cannot be deleted.
 */
export interface SaasProfile {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  category: string | null;
  fqdn_patterns: string[];
  enabled: number;
  appCount: number;
  created_at: string;
  updated_at: string | null;
}

export interface SaasProfileData {
  slug: string;
  name: string;
  description?: string | null;
  category?: string | null;
  fqdnPatterns: string[];
  enabled?: boolean;
}

/**
 * Identity group (VSNP-1160): a TENANT-scoped user group — what the policy
 * "Who → Groups" matches. Stored as a nested Keycloak group
 * "/t-<tenantId>/<name>", so two tenants may each own an "engineering".
 * `name` is the bare leaf the admin sees; the API tenant-qualifies it at
 * compile time. `ruleCount` > 0 blocks deletion.
 */
export interface IdentityGroup {
  id: string;
  name: string;
  path: string;
  memberCount: number;
  ruleCount: number;
}

// ---------- Identity providers (per-tenant external IdP, VSNP-1164) ----------

export type IdentityProviderType = 'azuread' | 'oidc';

// ---------- MDM integrations (VSNP-1169) ----------

export type MdmProviderId = 'intune' | 'jamf' | 'generic';
export type MdmMatchKey = 'email_hostname' | 'hostname' | 'mdm_device_id';

export interface MdmProviderMeta {
  id: MdmProviderId;
  label: string;
  requiredConfig: string[];
  needsSecret: boolean;
}

/** An integration as returned by the API — never carries the secret or token. */
export interface MdmIntegration {
  id: string;
  tenant_id: string;
  provider: MdmProviderId;
  display_name: string;
  config: Record<string, string>;
  hasSecret: boolean;
  match_key: MdmMatchKey;
  poll_interval_min: number;
  stale_after_hours: number;
  enabled: boolean;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  last_sync_devices: number | null;
  last_sync_matched: number | null;
  created_at: string;
  /** Generic provider only, present ONLY on the create response. */
  pushToken?: string;
}

export interface MdmIntegrationData {
  provider: MdmProviderId;
  displayName: string;
  config?: Record<string, string>;
  /** Write-only. */
  secret?: string;
  matchKey?: MdmMatchKey;
  pollIntervalMin?: number;
  staleAfterHours?: number;
  enabled?: boolean;
}

export interface MdmSyncResult {
  seen: number;
  matched: number;
  changed: number;
  unmatched: string[];
  invalid: number;
  recompiled: boolean;
}

export interface MdmTestResult {
  ok: boolean;
  devicesSeen: number;
  sample: string[];
  error?: string;
}

export interface IdentityProvider {
  id: string;
  tenantId: string;
  /** Keycloak broker alias: t-<tenantId>-<provider>. Also the kc_idp_hint value. */
  alias: string;
  providerType: IdentityProviderType;
  displayName: string;
  issuer: string;
  clientId: string;
  enabled: boolean;
  /** Redirect URI the customer registers in THEIR IdP. */
  brokerEndpoint: string;
  loginHint: string;
  /**
   * Sign-in routing (VSNP-1629, doc 28d §2): `all` sends every user of the
   * tenant to this provider (the default, and the only behaviour before V50);
   * `domains` sends only users of its verified `domains`. Absent on an older
   * API, which means `all`.
   */
  routing?: IdentityProviderRouting;
  /** The email domains it claims, with their verification state (absent on an older API). */
  domains?: IdentityProviderDomain[];
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
}

export type IdentityProviderRouting = 'all' | 'domains';

/**
 * An email domain a provider claims (doc 28d §3). It routes nobody until it is
 * verified: by DNS TXT (`verificationMethod: 'dns_txt'`) or by a SuperAdmin's
 * attestation (`'operator'`). A failed 24 h re-check is recorded in
 * `lastCheckError` but never unverifies it.
 */
export interface IdentityProviderDomain {
  id: string;
  providerId: string;
  /** The normalised A-label (lower-case ASCII, punycode for an IDN). */
  domain: string;
  /** The U-label when it differs from `domain` (an IDN), else null. */
  domainUnicode: string | null;
  status: 'pending' | 'verified';
  verificationMethod: 'dns_txt' | 'operator' | string | null;
  verifiedAt: string | null;
  verifiedBy: string | null;
  lastCheckAt: string | null;
  lastCheckError: string | null;
  createdAt: string | null;
  /** The record to publish for DNS verification (pending domains of the caller's own tenant only). */
  txtRecord?: { name: string; type: 'TXT'; value: string };
}

/** A claimed domain in the SuperAdmin's cross-tenant list (28d §10.4); never carries the TXT value. */
export interface ClaimedDomain extends Omit<IdentityProviderDomain, 'txtRecord'> {
  tenantId: string;
}

export type DomainCheck = { ok: true } | { ok: false; error: string };

/**
 * Who would change sign-in method if a provider's routing, enabled state,
 * existence or domains changed (doc 28d §10.2): the confirm dialog shows it,
 * and the API refuses a write that moves users without `confirm=true`.
 */
export interface RoutingPreview {
  tenantId: string;
  mode: { before: 'local' | 'all' | 'domains'; after: 'local' | 'all' | 'domains' };
  users: { total: number; toProvider: number; toPassword: number; providerChanged: number; unchanged: number };
  /** Users moving provider → password with no local password (they could not sign in); named up to 20. */
  strandedBrokeredOnly: { count: number; emails: string[] };
  /** App devices in Access groups whose effective fail mode would change. */
  devices: { failModeChanged: number; toOpen: number; toClosed: number };
  /**
   * Users whose PRE-SIGN-IN answer changes (VSNP-1707): what the new
   * email-first apps get from the email. A verified domain finds its provider
   * in both routing modes, so a write can change this while the route after
   * registration stays put (e.g. removing a verified domain from an all-mode
   * provider). Absent on an older API.
   */
  discoveryChanges?: {
    toProvider: number;
    toPassword: number;
    providerChanged: number;
    strandedBrokeredOnly: { count: number; emails: string[] };
  };
  discoveryChanged?: boolean;
  /** Some user would change sign-in route, after registration or before sign-in (a write that does needs confirm). */
  changed: boolean;
}

/** A hypothetical change for GET /identity-providers/routing-preview. */
export interface RoutingChangeQuery {
  providerId: string;
  routing?: IdentityProviderRouting;
  enabled?: boolean;
  delete?: boolean;
  /** A domain the provider would hold, verified. */
  addDomain?: string;
  /** A domain (or its row id) the provider would no longer hold. */
  removeDomain?: string;
}

/**
 * The preview a route-changing write was refused with (409 CONFIRM_REQUIRED,
 * doc 28d §10.2), or null for any other error.
 */
export function confirmRequiredPreview(err: unknown): RoutingPreview | null {
  const r = (err as { response?: { status?: number; data?: { error?: { code?: string; details?: { preview?: RoutingPreview } } } } } | null)
    ?.response;
  if (r?.status !== 409 || r.data?.error?.code !== 'CONFIRM_REQUIRED') return null;
  return r.data.error.details?.preview ?? null;
}

export interface CreateIdentityProviderData {
  providerType: IdentityProviderType;
  displayName: string;
  azureTenantId?: string;
  issuer?: string;
  clientId: string;
  clientSecret: string;
}

export interface UpdateIdentityProviderData {
  displayName?: string;
  clientId?: string;
  /** Write-only; omit to keep the secret held in Keycloak. */
  clientSecret?: string;
  enabled?: boolean;
  /** Sign-in routing (28d §4.5): `domains` needs at least one verified domain. */
  routing?: IdentityProviderRouting;
  /** Apply a change that moves users to another sign-in method (28d §10.2). */
  confirm?: boolean;
}

export type IdentityProviderTestResult =
  | { ok: true; latencyMs: number; issuer?: string; authorizationUrl?: string; tokenUrl?: string; jwksUrl?: string }
  | { ok: false; latencyMs: number; error: string };

/** An IdP-side group id linked to one of the tenant's identity groups. */
export interface ExternalGroupMapping {
  id: string;
  identityProviderId: string;
  identityProviderAlias: string;
  identityProviderName: string;
  claim: string;
  externalGroupId: string;
  group: string;
}

export interface IdentityGroupMember {
  vpnUserId: string;
  email: string;
  displayName: string | null;
  keycloakSub: string;
}

/** Posture operators (VSNP-1173). eq/neq are also expressible as a bare string. */
export type PostureOp = 'eq' | 'neq' | 'in' | 'not_in' | 'gte' | 'lte' | 'gt' | 'lt';

/** A posture requirement: a bare string (equality) or an operator clause. */
export type PosturePredicate = string | { op: PostureOp; values: string[] };

export interface AccessPolicyRule {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  priority: number;
  enabled: number;
  subject_groups: string[] | null;
  subject_users: string[] | null;
  require_managed_device: number;
  /** DEVICE: the Posture Profile this policy enforces (V31); null = none. */
  posture_profile_id: string | null;
  /**
   * LEGACY inline posture requirements (pre-profile rules): fact key → predicate.
   * A predicate is a bare string (equality) or an operator clause `{ op, values }`
   * (VSNP-1173). Ignored at compile when posture_profile_id is set; the UI no
   * longer writes it.
   */
  device_posture: Record<string, PosturePredicate> | null;
  app_ids: string[] | null;
  /** WHAT: target Application Groups by id; expanded to member apps at compile. */
  app_group_ids: string[] | null;
  app_tags: string[] | null;
  http_methods: string[] | null;
  path_prefixes: string[] | null;
  time_window: PolicyTimeWindow | null;
  geo_allow: string[] | null;
  geo_deny: string[] | null;
  action: 'allow' | 'deny';
  created_at: string;
}

export interface PolicyTimeWindow {
  days?: ('mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun')[];
  start?: string;
  end?: string;
  tz?: string;
}

export interface CreatePolicyRuleData {
  name: string;
  description?: string;
  priority?: number;
  enabled?: boolean;
  subjectGroups?: string[];
  subjectUsers?: string[];
  requireManagedDevice?: boolean;
  /** Posture Profile id; null clears the device constraint. */
  postureProfileId?: string | null;
  /** Legacy inline posture — only sent to clear it (`{}`) when a profile replaces it. */
  devicePosture?: Record<string, PosturePredicate>;
  appIds?: string[];
  appGroupIds?: string[];
  appTags?: string[];
  httpMethods?: string[];
  pathPrefixes?: string[];
  timeWindow?: PolicyTimeWindow;
  geoAllow?: string[];
  geoDeny?: string[];
  action: 'allow' | 'deny';
}

/** Metadata about a compiled + signed bundle. The payload is never sent here. */
export interface AccessPolicyVersion {
  id: string;
  version: string;
  epoch: number;
  sha256: string;
  keyId: string;
  ruleCount: number;
  status?: 'draft' | 'active' | 'superseded';
  compilerVersion?: string;
  compiledAt: string;
  createdBy: string | null;
}

export interface CompileResult {
  policyId: string;
  version: string;
  epoch: number;
  sha256: string;
  ruleCount: number;
  unchanged: boolean;
}

// ---------- Devices ----------

/** No dedicated `retire` transition exists server-side; lifecycle is enrol → active → revoked. */
export type AccessDeviceStatus = 'pending_approval' | 'active' | 'revoked' | 'retired';
export type AccessDevicePlatform = 'windows' | 'macos' | 'ios' | 'android' | 'linux' | 'unknown';

/**
 * What the gateway concluded about a session's token at admission
 * (access-common/reason.TokenBinding*, design 23 §3.2).
 *
 * Two independent findings share one enum: the cryptographic confirmation
 * claim (`bound` / `unbound` / `mismatch`) and the orchestrator's
 * device→subject allowlist (`device_subject` / `device_unassigned`).
 */
export type TokenBindingResult =
  | 'bound'
  | 'unbound'
  | 'mismatch'
  | 'device_subject'
  | 'device_unassigned';

/** The tenant's device→subject enforcement mode. `audit` is the shipped default. */
export type DeviceSubjectMode = 'audit' | 'enforce';

export interface DeviceBindingSettings {
  tenantId: string;
  subjectMode: DeviceSubjectMode;
  /** What the platform ships as; the UI renders anything else as a deviation. */
  default: DeviceSubjectMode;
}

/** Where an effective sign-in value came from (doc 28 §4.10). */
export type SignInSource = 'user' | 'group' | 'tenant' | 'default';

/** One level's sign-in values, seconds; null = this level sets nothing. */
export interface SignInLevel {
  reauthInterval: number | null;
  tokenLifetime: number | null;
}

/** The resolved sign-in lifetime policy and where each value came from. */
export interface EffectiveSignIn {
  reauthInterval: number;
  reauthIntervalSource: SignInSource;
  tokenLifetime: number;
  tokenLifetimeSource: SignInSource;
}

/** The limits the API enforces, served with every view so the UI never guesses. */
export interface SignInPolicyLimits {
  defaults: { reauthInterval: number; tokenLifetime: number };
  ranges: {
    reauthInterval: { min: number; max: number };
    tokenLifetime: { min: number; max: number };
  };
  presets: { reauthInterval: number[] };
}

export interface TenantSignInPolicy extends SignInPolicyLimits {
  tenantId: string;
  tenant: SignInLevel;
  effective: EffectiveSignIn;
}

export interface UserSignInPolicy extends SignInPolicyLimits {
  userId: string;
  tenantId: string;
  user: SignInLevel;
  tenant: SignInLevel;
  defaultGroup: ({ id: string; name: string } & SignInLevel) | null;
  effective: EffectiveSignIn;
  byGroup: Array<{ groupId: string; groupName: string; effective: EffectiveSignIn }>;
}

export interface EffectiveSignInView extends SignInPolicyLimits {
  tenantId: string;
  tenant: SignInLevel;
  group: ({ id: string; name: string } & SignInLevel) | null;
  user: SignInLevel | null;
  effective: EffectiveSignIn;
}

/**
 * What the tenant's sign-in route allows for the kill switch (doc 28 §4.9,
 * option A of 2026-10-07): a tenant that signs in through an external IdP
 * cannot use fail closed, and its default fail mode is open.
 */
export interface TenantConnectPolicy {
  tenantId: string;
  externalIdp: boolean;
  providerType: 'azuread' | 'oidc' | null;
  failClosedAllowed: boolean;
  defaultFailMode: 'closed' | 'open';
  /** The API's own explanation, when fail closed is not available. */
  failClosedUnavailableReason?: string;
  /**
   * Present only when the tenant routes sign-in by email domain (doc 28d §7.2):
   * fail closed is then available, and only the members a provider routes fail
   * open.
   */
  routing?: 'domains';
  /**
   * Domain mode only: per Access group id, how many of its devices belong to an
   * owner who signs in through a provider (and so fail open when the group
   * locks down and fails closed).
   */
  providerMembers?: Record<string, number>;
}

/** A tenant- or user-level write: a number sets, null removes, absent leaves it. */
export interface SignInPolicyWrite {
  reauthInterval?: number | null;
  tokenLifetime?: number | null;
}

/** One identity permitted to act as a device. See `AccessDevice.subjects`. */
export interface DeviceSubject {
  kind: 'sub' | 'email' | 'username';
  value: string;
  /** Who added it — an operator's username, `enrol:…`, or `migration:V35`. */
  addedBy?: string;
  addedAt?: string;
}

export interface AccessDevice {
  id: string;
  tenant_id: string;
  /** The device CN — its stable identity, carried in the client certificate. */
  device_id: string;
  device_name: string | null;
  user_email: string | null;
  user_id: string | null;
  /** The owner's IdP subject, copied onto the row at enrolment (V35). */
  keycloak_sub: string | null;
  /**
   * The EXPLICIT list of identities allowed to act as this device — what the
   * owner-only re-enrolment lock tests a caller against (VSNP-1294), and what a
   * shared / kiosk device is (design 23 §9.4 decision D).
   *
   * `null` on a row that predates V35 and had no resolvable owner; `[]` when an
   * administrator has deliberately left it empty. Both mean nobody may enrol it
   * without a reassignment.
   */
  subjects: DeviceSubject[] | null;
  platform: AccessDevicePlatform;
  os_version: string | null;
  client_version: string | null;
  /** Verified posture facts (fact → value). Empty until the device reports & the gateway verifies. */
  posture: Record<string, unknown> | null;
  posture_checked_at: string | null;
  certificate_serial_number: string | null;
  certificate_issued_at: string | null;
  certificate_expires_at: string | null;
  certificate_thumbprint_s256: string | null;
  posture_public_key: string | null;
  status: AccessDeviceStatus;
  enrolled_at: string | null;
  last_seen: string | null;
  /**
   * The most recent token-binding outcome observed for this device (WP8B,
   * V36) — one of `TokenBindingResult`.
   *
   * `null` means the device has not connected since device-subject checking
   * shipped, which is NOT the same as `'unbound'`: one is "we have not
   * looked", the other is "we looked and the IdP bound nothing".
   */
  last_token_binding: TokenBindingResult | null;
  last_token_binding_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  revocation_reason: string | null;
  created_at: string;
  updated_at: string;
}

/** Seat usage against `vpn_users.max_devices` (0 = unlimited; null = owner has no VPN-user row). */
export interface DeviceQuota {
  maxDevices: number | null;
  used: number;
  unlimited: boolean;
}

/** A VPN user's Access devices + quota — Administration → Users → user → Access Devices (VSNP-1163). */
export interface UserDevices {
  user: { id: string; email: string; displayName: string | null; tenantId: string };
  quota: DeviceQuota;
  items: AccessDevice[];
}

export interface ListDevicesParams {
  status?: AccessDeviceStatus;
  userEmail?: string;
  platform?: AccessDevicePlatform;
  limit?: number;
  offset?: number;
}

export interface UpdateDeviceData {
  deviceName?: string;
  osVersion?: string;
  clientVersion?: string;
}

/**
 * Reassignment payload. The new owner is named by email or by VPN-user id;
 * `subjects` adds EXTRA identities for a shared / kiosk device (the new owner
 * is always seeded server-side, so it never repeats them).
 */
export interface ReassignDeviceData {
  userEmail?: string;
  userId?: string;
  subjects?: { kind: DeviceSubject['kind']; value: string }[];
}

/**
 * Reported by /:id/revoke and /:id/reassign — how far the revocation reached.
 *
 * These are the server's `FanoutResult` field names. They previously read
 * `{ published, gateways }` here, which no endpoint has ever returned, so the
 * revoke toast rendered "Pushed to undefined gateway(s)". Corrected alongside
 * the reassignment work, which returns the same shape.
 */
export interface RevocationFanout {
  /** Gateways the revocation was addressed to. */
  targeted: number;
  /** Gateways the broker accepted the publish for. */
  delivered: number;
}

// ============================================
// Service
// ============================================

const accessService = {
  // ---------- Gateways (SuperAdmin) ----------

  listGateways: async (params?: { status?: string; region?: string; limit?: number }) => {
    const response = await api.get(`${BASE}/gateways`, { params });
    return unwrap<PaginatedResponse<AccessGateway>>(response);
  },

  getGatewayFleetSummary: async () => {
    const response = await api.get(`${BASE}/gateways/summary`);
    return unwrap<GatewayFleetSummary>(response);
  },

  getGateway: async (id: string) => {
    const response = await api.get(`${BASE}/gateways/${id}`);
    return unwrap<AccessGateway>(response);
  },

  createGateway: async (data: CreateGatewayData) => {
    const response = await api.post(`${BASE}/gateways`, data);
    return unwrap<AccessGateway>(response);
  },

  updateGateway: async (id: string, data: UpdateGatewayData) => {
    const response = await api.put(`${BASE}/gateways/${id}`, data);
    return unwrap<AccessGateway>(response);
  },

  approveGateway: async (id: string) => {
    const response = await api.post(`${BASE}/gateways/${id}/approve`);
    return unwrap<AccessGateway>(response);
  },

  decommissionGateway: async (id: string) => {
    const response = await api.delete(`${BASE}/gateways/${id}`);
    return unwrap<AccessGateway>(response);
  },

  // Gateway ↔ tenant assignments (VSNP-1126). Mutations are SuperAdmin-only (D3).

  listGatewayTenants: async (id: string) => {
    const response = await api.get(`${BASE}/gateways/${id}/tenants`);
    return unwrap<GatewayTenantAssignment[]>(response);
  },

  assignGatewayTenant: async (id: string, tenantId: string) => {
    const response = await api.post(`${BASE}/gateways/${id}/tenants`, { tenantId });
    return unwrap<GatewayTenantAssignment>(response);
  },

  removeGatewayTenant: async (id: string, tenantId: string) => {
    await api.delete(`${BASE}/gateways/${id}/tenants/${encodeURIComponent(tenantId)}`);
  },

  // ---------- Connectors ----------

  listConnectors: async (params?: { status?: string; connectorGroup?: string; limit?: number }) => {
    const response = await api.get(`${BASE}/connectors`, {
      params: { ...tenantParams(), ...params },
    });
    return unwrap<PaginatedResponse<AppConnector>>(response);
  },

  listConnectorGroups: async () => {
    const response = await api.get(`${BASE}/connectors/groups`, { params: tenantParams() });
    return unwrap<ConnectorGroupSummary[]>(response);
  },

  getConnectorGroup: async (name: string) => {
    const response = await api.get(`${BASE}/connectors/groups/${encodeURIComponent(name)}`, {
      params: tenantParams(),
    });
    return unwrap<ConnectorGroupDetail>(response);
  },

  createConnectorGroup: async (data: { name: string; description?: string }) => {
    const response = await api.post(`${BASE}/connectors/groups`, data, { params: tenantParams() });
    return unwrap<ConnectorGroupSummary>(response);
  },

  updateConnectorGroup: async (id: string, data: { name?: string; description?: string | null }) => {
    const response = await api.put(`${BASE}/connectors/groups/${id}`, data, { params: tenantParams() });
    return unwrap<ConnectorGroupSummary>(response);
  },

  deleteConnectorGroup: async (id: string) => {
    await api.delete(`${BASE}/connectors/groups/${id}`, { params: tenantParams() });
  },

  getConnector: async (id: string) => {
    const response = await api.get(`${BASE}/connectors/${id}`, { params: tenantParams() });
    return unwrap<AppConnector>(response);
  },

  createConnector: async (data: CreateConnectorData) => {
    const response = await api.post(`${BASE}/connectors`, data, { params: tenantParams() });
    return unwrap<AppConnector>(response);
  },

  updateConnector: async (id: string, data: UpdateConnectorData) => {
    const response = await api.put(`${BASE}/connectors/${id}`, data, { params: tenantParams() });
    return unwrap<AppConnector>(response);
  },

  approveConnector: async (id: string) => {
    const response = await api.post(`${BASE}/connectors/${id}/approve`, undefined, {
      params: tenantParams(),
    });
    return unwrap<AppConnector>(response);
  },

  decommissionConnector: async (id: string) => {
    const response = await api.delete(`${BASE}/connectors/${id}`, { params: tenantParams() });
    return unwrap<AppConnector>(response);
  },

  // ---------- Apps ----------

  listApps: async (params?: { appType?: string; connectorGroup?: string; limit?: number }) => {
    const response = await api.get(`${BASE}/apps`, { params: { ...tenantParams(), ...params } });
    return unwrap<PaginatedResponse<AccessApp>>(response);
  },

  createApp: async (data: CreateAppData) => {
    const response = await api.post(`${BASE}/apps`, data, { params: tenantParams() });
    return unwrap<AccessApp>(response);
  },

  updateApp: async (id: string, data: UpdateAppData) => {
    const response = await api.put(`${BASE}/apps/${id}`, data, { params: tenantParams() });
    return unwrap<AccessApp>(response);
  },

  deleteApp: async (id: string) => {
    await api.delete(`${BASE}/apps/${id}`, { params: tenantParams() });
  },

  // ---------- Application groups (tenant-scoped, V28) ----------

  listAppGroups: async () => {
    const response = await api.get(`${BASE}/app-groups`, { params: tenantParams() });
    return unwrap<AppGroup[]>(response);
  },

  createAppGroup: async (data: AppGroupData) => {
    const response = await api.post(`${BASE}/app-groups`, data, { params: tenantParams() });
    return unwrap<AppGroup>(response);
  },

  updateAppGroup: async (id: string, data: Partial<AppGroupData>) => {
    const response = await api.put(`${BASE}/app-groups/${id}`, data, { params: tenantParams() });
    return unwrap<AppGroup>(response);
  },

  deleteAppGroup: async (id: string) => {
    await api.delete(`${BASE}/app-groups/${id}`, { params: tenantParams() });
  },

  // ---------- Device posture profiles (tenant-scoped, V31) ----------

  listPostureProfiles: async () => {
    const response = await api.get(`${BASE}/posture-profiles`, { params: tenantParams() });
    return unwrap<PostureProfile[]>(response);
  },

  createPostureProfile: async (data: PostureProfileData) => {
    const response = await api.post(`${BASE}/posture-profiles`, data, { params: tenantParams() });
    return unwrap<PostureProfile>(response);
  },

  /** Edit in place. Policies using the profile pick up the change on the next compile. */
  updatePostureProfile: async (id: string, data: Partial<PostureProfileData>) => {
    const response = await api.put(`${BASE}/posture-profiles/${id}`, data, { params: tenantParams() });
    return unwrap<PostureProfile>(response);
  },

  deletePostureProfile: async (id: string) => {
    await api.delete(`${BASE}/posture-profiles/${id}`, { params: tenantParams() });
  },

  // ---------- SaaS catalog (SuperAdmin-managed, VSNP-1150) ----------

  listSaasCatalog: async () => {
    const response = await api.get(`${BASE}/saas-catalog`);
    return unwrap<SaasProfile[]>(response);
  },

  createSaasProfile: async (data: SaasProfileData) => {
    const response = await api.post(`${BASE}/saas-catalog`, data);
    return unwrap<SaasProfile>(response);
  },

  updateSaasProfile: async (id: string, data: Partial<SaasProfileData>) => {
    const response = await api.put(`${BASE}/saas-catalog/${id}`, data);
    return unwrap<SaasProfile>(response);
  },

  deleteSaasProfile: async (id: string) => {
    await api.delete(`${BASE}/saas-catalog/${id}`);
  },

  // ---------- Identity groups (tenant-scoped, VSNP-1160/1161) ----------
  // Tenant-scoped like apps: tenantParams() carries the selected tenant for a
  // SuperAdmin; a TenantAdmin is scoped server-side to their own tenant.

  listIdentityGroups: async () => {
    const response = await api.get(`${BASE}/identity-groups`, { params: tenantParams() });
    return unwrap<IdentityGroup[]>(response);
  },

  createIdentityGroup: async (name: string) => {
    const response = await api.post(`${BASE}/identity-groups`, { name }, { params: tenantParams() });
    return unwrap<IdentityGroup>(response);
  },

  deleteIdentityGroup: async (id: string) => {
    await api.delete(`${BASE}/identity-groups/${id}`, { params: tenantParams() });
  },

  listIdentityGroupMembers: async (id: string) => {
    const response = await api.get(`${BASE}/identity-groups/${id}/members`, { params: tenantParams() });
    return unwrap<IdentityGroupMember[]>(response);
  },

  /** Adds a same-tenant VPN user; resolves to the updated member list. */
  addIdentityGroupMember: async (id: string, vpnUserId: string) => {
    const response = await api.post(`${BASE}/identity-groups/${id}/members`, { vpnUserId }, { params: tenantParams() });
    return unwrap<IdentityGroupMember[]>(response);
  },

  removeIdentityGroupMember: async (id: string, vpnUserId: string) => {
    await api.delete(`${BASE}/identity-groups/${id}/members/${vpnUserId}`, { params: tenantParams() });
  },

  // ---------- Policies ----------

  listPolicyRules: async () => {
    const response = await api.get(`${BASE}/policies/rules`, { params: tenantParams() });
    return unwrap<AccessPolicyRule[]>(response);
  },

  createPolicyRule: async (data: CreatePolicyRuleData) => {
    const response = await api.post(`${BASE}/policies/rules`, data, { params: tenantParams() });
    return unwrap<AccessPolicyRule>(response);
  },

  /** Edit an authored rule in place (partial). Inert until the bundle is recompiled. */
  updatePolicyRule: async (id: string, data: Partial<CreatePolicyRuleData>) => {
    const response = await api.put(`${BASE}/policies/rules/${id}`, data, { params: tenantParams() });
    return unwrap<AccessPolicyRule>(response);
  },

  deletePolicyRule: async (id: string) => {
    await api.delete(`${BASE}/policies/rules/${id}`, { params: tenantParams() });
  },

  /**
   * The tenant's active bundle. `tenantId` names the tenant explicitly — for a
   * SuperAdmin in "All Tenants", where there is no selected tenant to default
   * to and the API rightly answers 400 without one (VSNP-1434).
   */
  getActivePolicy: async (tenantId?: string) => {
    const response = await api.get(`${BASE}/policies/active`, {
      params: tenantId ? { tenantId } : tenantParams(),
    });
    return unwrap<AccessPolicyVersion | null>(response);
  },

  listPolicyVersions: async (limit = 20) => {
    const response = await api.get(`${BASE}/policies/versions`, {
      params: { ...tenantParams(), limit },
    });
    return unwrap<AccessPolicyVersion[]>(response);
  },

  compilePolicy: async () => {
    const response = await api.post(`${BASE}/policies/compile`, undefined, {
      params: tenantParams(),
    });
    return unwrap<CompileResult>(response);
  },

  // ---------- Identity providers (VSNP-1164) ----------

  // ---------- MDM integrations (tenant-scoped, VSNP-1169) ----------

  listMdmProviders: async () => {
    const response = await api.get(`${BASE}/mdm-integrations/providers`);
    return unwrap<MdmProviderMeta[]>(response);
  },

  listMdmIntegrations: async () => {
    const response = await api.get(`${BASE}/mdm-integrations`, { params: tenantParams() });
    return unwrap<MdmIntegration[]>(response);
  },

  /** For the generic provider the response carries the ONE-TIME pushToken. */
  createMdmIntegration: async (data: MdmIntegrationData) => {
    const response = await api.post(`${BASE}/mdm-integrations`, data, { params: tenantParams() });
    return unwrap<MdmIntegration>(response);
  },

  updateMdmIntegration: async (id: string, data: Partial<Omit<MdmIntegrationData, 'provider'>>) => {
    const response = await api.put(`${BASE}/mdm-integrations/${id}`, data, { params: tenantParams() });
    return unwrap<MdmIntegration>(response);
  },

  deleteMdmIntegration: async (id: string) => {
    await api.delete(`${BASE}/mdm-integrations/${id}`, { params: tenantParams() });
  },

  testMdmIntegration: async (id: string) => {
    const response = await api.post(`${BASE}/mdm-integrations/${id}/test`, {}, { params: tenantParams() });
    return unwrap<MdmTestResult>(response);
  },

  syncMdmIntegration: async (id: string) => {
    const response = await api.post(`${BASE}/mdm-integrations/${id}/sync`, {}, { params: tenantParams() });
    return unwrap<MdmSyncResult>(response);
  },

  /** The tenant's providers. `tenantId` overrides the selected tenant (a SuperAdmin reading a user's tenant). */
  listIdentityProviders: async (tenantId?: string) => {
    const response = await api.get(`${BASE}/identity-providers`, { params: tenantId ? { tenantId } : tenantParams() });
    return unwrap<IdentityProvider[]>(response);
  },

  createIdentityProvider: async (data: CreateIdentityProviderData) => {
    const response = await api.post(`${BASE}/identity-providers`, data, { params: tenantParams() });
    return unwrap<IdentityProvider>(response);
  },

  updateIdentityProvider: async (id: string, data: UpdateIdentityProviderData) => {
    const response = await api.put(`${BASE}/identity-providers/${id}`, data, { params: tenantParams() });
    return unwrap<IdentityProvider>(response);
  },

  /** `confirm`: apply it although users change sign-in method (a domain-mode tenant, 28d §7.3). */
  deleteIdentityProvider: async (id: string, opts: { confirm?: boolean } = {}) => {
    await api.delete(`${BASE}/identity-providers/${id}`, {
      params: { ...tenantParams(), ...(opts.confirm ? { confirm: 'true' } : {}) },
    });
  },

  // ---------- Email domains and sign-in routing (VSNP-1629, doc 28d §4.5) ----------

  /** Claim a domain: it starts pending, with the DNS TXT record to publish. 409 DOMAIN_CLAIMED when taken. */
  addIdentityProviderDomain: async (providerId: string, domain: string) => {
    const response = await api.post(`${BASE}/identity-providers/${providerId}/domains`, { domain }, { params: tenantParams() });
    return unwrap<IdentityProviderDomain>(response);
  },

  /** Run the DNS TXT check now (a missing or wrong record answers 200 with `check.ok: false`). */
  verifyIdentityProviderDomain: async (providerId: string, domainId: string) => {
    const response = await api.post(`${BASE}/identity-providers/${providerId}/domains/${domainId}/verify`, undefined, {
      params: tenantParams(),
    });
    return unwrap<{ domain: IdentityProviderDomain; check: DomainCheck }>(response);
  },

  /** SuperAdmin only: verify by attestation, with a reason (audited). */
  attestIdentityProviderDomain: async (providerId: string, domainId: string, reason: string) => {
    const response = await api.post(`${BASE}/identity-providers/${providerId}/domains/${domainId}/attest`, { reason }, {
      params: tenantParams(),
    });
    return unwrap<IdentityProviderDomain>(response);
  },

  /**
   * Remove a domain. Without `confirm`, a removal that moves users answers 409
   * CONFIRM_REQUIRED with the preview (see confirmRequiredPreview). `tenantId`
   * names another tenant (a SuperAdmin resolving a claim, 28d §10.4).
   */
  removeIdentityProviderDomain: async (
    providerId: string,
    domainId: string,
    opts: { confirm?: boolean; reason?: string; tenantId?: string } = {},
  ) => {
    const response = await api.delete(`${BASE}/identity-providers/${providerId}/domains/${domainId}`, {
      params: {
        ...(opts.tenantId ? { tenantId: opts.tenantId } : tenantParams()),
        ...(opts.confirm ? { confirm: 'true' } : {}),
        ...(opts.reason ? { reason: opts.reason } : {}),
      },
    });
    return unwrap<{ preview: RoutingPreview }>(response);
  },

  /** Read-only: who would change sign-in method (the confirm dialog's counts, 28d §10.2). */
  getRoutingPreview: async (q: RoutingChangeQuery, tenantId?: string) => {
    const response = await api.get(`${BASE}/identity-providers/routing-preview`, {
      params: {
        ...(tenantId ? { tenantId } : tenantParams()),
        providerId: q.providerId,
        ...(q.routing ? { routing: q.routing } : {}),
        ...(q.enabled !== undefined ? { enabled: String(q.enabled) } : {}),
        ...(q.delete ? { delete: 'true' } : {}),
        ...(q.addDomain ? { addDomain: q.addDomain } : {}),
        ...(q.removeDomain ? { removeDomain: q.removeDomain } : {}),
      },
    });
    return unwrap<RoutingPreview>(response);
  },

  /** SuperAdmin only: every claimed domain across tenants (28d §10.4). */
  listClaimedDomains: async () => {
    const response = await api.get(`${BASE}/identity-providers/claimed-domains`);
    return unwrap<ClaimedDomain[]>(response);
  },

  testIdentityProvider: async (id: string) => {
    const response = await api.post(`${BASE}/identity-providers/${id}/test`, undefined, {
      params: tenantParams(),
    });
    return unwrap<IdentityProviderTestResult>(response);
  },

  listExternalGroupMappings: async (groupId: string) => {
    const response = await api.get(`${BASE}/identity-groups/${groupId}/external-mappings`, { params: tenantParams() });
    return unwrap<ExternalGroupMapping[]>(response);
  },

  addExternalGroupMapping: async (groupId: string, data: { identityProviderId: string; externalGroupId: string; claim?: string }) => {
    const response = await api.post(`${BASE}/identity-groups/${groupId}/external-mappings`, data, { params: tenantParams() });
    return unwrap<ExternalGroupMapping>(response);
  },

  removeExternalGroupMapping: async (groupId: string, mapperId: string) => {
    await api.delete(`${BASE}/identity-groups/${groupId}/external-mappings/${mapperId}`, { params: tenantParams() });
  },

  // ---------- Devices ----------

  listDevices: async (params?: ListDevicesParams) => {
    const response = await api.get(`${BASE}/devices`, { params: { ...tenantParams(), ...params } });
    return unwrap<PaginatedResponse<AccessDevice>>(response);
  },

  getDevice: async (id: string) => {
    const response = await api.get(`${BASE}/devices/${id}`, { params: tenantParams() });
    return unwrap<AccessDevice>(response);
  },

  updateDevice: async (id: string, data: UpdateDeviceData) => {
    const response = await api.put(`${BASE}/devices/${id}`, data, { params: tenantParams() });
    return unwrap<AccessDevice>(response);
  },

  // The optional `tenantId` on the device actions lets a SuperAdmin act from a
  // cross-tenant page (the VPN-user detail, browsable on All Tenants) where
  // tenantParams() would be empty and the API would 400.
  approveDevice: async (id: string, tenantId?: string) => {
    const response = await api.post(`${BASE}/devices/${id}/approve`, undefined, {
      params: { ...tenantParams(), ...(tenantId ? { tenantId } : {}) },
    });
    return unwrap<AccessDevice>(response);
  },

  /** Revoke fans the revocation out to the fleet so live sessions drop in seconds. */
  revokeDevice: async (id: string, reason: string, tenantId?: string) => {
    const response = await api.post(`${BASE}/devices/${id}/revoke`, { reason }, {
      params: { ...tenantParams(), ...(tenantId ? { tenantId } : {}) },
    });
    return unwrap<AccessDevice & { revocationFanout?: RevocationFanout }>(response);
  },

  /**
   * Move a device to a new owner (tenant admin only).
   *
   * Destructive on purpose: the server revokes the current certificate through
   * the same path the Revoke action uses — live sessions drop fleet-wide — and
   * returns the device to `pending_approval`, so the new owner must enrol and
   * an admin must approve. The confirmation dialog says so before it is called.
   */
  reassignDevice: async (id: string, data: ReassignDeviceData, tenantId?: string) => {
    const response = await api.post(`${BASE}/devices/${id}/reassign`, data, {
      params: { ...tenantParams(), ...(tenantId ? { tenantId } : {}) },
    });
    return unwrap<AccessDevice & { revocationFanout?: RevocationFanout; certificateRevoked?: boolean }>(response);
  },

  deleteDevice: async (id: string, tenantId?: string) => {
    await api.delete(`${BASE}/devices/${id}`, { params: { ...tenantParams(), ...(tenantId ? { tenantId } : {}) } });
  },

  // ---------- Device binding (WP8B, design 23 §3.1) ----------

  getDeviceBinding: async () => {
    const response = await api.get(`${BASE}/device-binding`, { params: tenantParams() });
    return unwrap<DeviceBindingSettings>(response);
  },

  /**
   * Flip the tenant's device→subject mode. The server RECOMPILES the signed
   * bundle as part of the call — the mode only exists at a gateway as a field
   * in that bundle, so a write without a recompile would change nothing while
   * appearing to.
   */
  setDeviceBinding: async (subjectMode: DeviceSubjectMode) => {
    const response = await api.put(`${BASE}/device-binding`, { subjectMode }, { params: tenantParams() });
    return unwrap<{ tenantId: string; subjectMode: DeviceSubjectMode; compile: { version: string; unchanged: boolean } }>(
      response,
    );
  },

  // ---------- Sign-in lifetime policy (doc 28 §4.10) ----------
  //
  // How often a Secure Access user must sign in again, and how long each
  // access token lives: tenant level here, overridden per Access device group
  // (access_tunnel.signIn) and per VPN user. Every write recompiles the signed
  // bundle, because the gateways enforce the values from it.

  getSignInPolicy: async () => {
    const response = await api.get(`${BASE}/sign-in-policy`, { params: tenantParams() });
    return unwrap<TenantSignInPolicy>(response);
  },

  /** Whether fail closed is available to the tenant (not with an external IdP). */
  getConnectPolicy: async () => {
    const response = await api.get(`${BASE}/connect-policy`, { params: tenantParams() });
    return unwrap<TenantConnectPolicy>(response);
  },

  setSignInPolicy: async (body: SignInPolicyWrite) => {
    const response = await api.put(`${BASE}/sign-in-policy`, body, { params: tenantParams() });
    return unwrap<TenantSignInPolicy & { compile: { version: string; unchanged: boolean } }>(response);
  },

  /** What a device group (and optionally one of its users) resolves to. */
  getEffectiveSignIn: async (opts: { deviceGroupId?: string; userId?: string } = {}) => {
    const response = await api.get(`${BASE}/sign-in-policy/effective`, {
      params: {
        ...tenantParams(),
        ...(opts.deviceGroupId ? { deviceGroupId: opts.deviceGroupId } : {}),
        ...(opts.userId ? { userId: opts.userId } : {}),
      },
    });
    return unwrap<EffectiveSignInView>(response);
  },

  getUserSignInPolicy: async (vpnUserId: string) => {
    const response = await api.get(`${BASE}/sign-in-policy/users/${vpnUserId}`, { params: tenantParams() });
    return unwrap<UserSignInPolicy>(response);
  },

  setUserSignInPolicy: async (vpnUserId: string, body: SignInPolicyWrite) => {
    const response = await api.put(`${BASE}/sign-in-policy/users/${vpnUserId}`, body, { params: tenantParams() });
    return unwrap<UserSignInPolicy & { compile: { version: string; unchanged: boolean } }>(response);
  },

  /** A VPN user's Access devices + seat usage. Tenant is resolved server-side from the user row. */
  listUserDevices: async (vpnUserId: string) => {
    const response = await api.get(`${BASE}/users/${vpnUserId}/devices`, { params: tenantParams() });
    return unwrap<UserDevices>(response);
  },

  // ---------- Provisioning claims (registration tokens) ----------
  //
  // NOTE: /api/v2/provision/* returns BARE JSON, not the {success,data}
  // envelope the /access routes use — no unwrap() here. A SuperAdmin names
  // the tenant explicitly (query for reads, body for the mint); tenant admins
  // are pinned server-side.

  /**
   * Mint a one-time registration token for a pre-registered node row. The
   * response is the ONLY place the full token appears — the list endpoint
   * masks it — so the caller must display it immediately (wizard step 2).
   */
  mintNodeClaim: async (
    deviceType: 'gateway' | 'app_connector',
    deviceId: string,
    expiresInDays = 7,
  ) => {
    const response = await api.post('/api/v2/provision/claims', {
      deviceType,
      deviceId,
      expiresInDays,
      ...tenantParams(),
    });
    return response as unknown as MintedClaim;
  },

  listNodeClaims: async (params?: {
    deviceType?: 'gateway' | 'app_connector';
    status?: ProvisionClaim['status'];
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<ProvisionClaim>> => {
    const response = await api.get('/api/v2/provision/claims', {
      params: { ...tenantParams(), ...params },
    });
    // /provision returns bare JSON: { claims, total, limit, offset } (VSNP-1094).
    const r = response as unknown as {
      claims?: ProvisionClaim[];
      total?: number;
      limit?: number;
      offset?: number;
    };
    const items = r.claims ?? [];
    return {
      items,
      total: r.total ?? items.length,
      limit: r.limit ?? items.length,
      offset: r.offset ?? 0,
    };
  },

  revokeNodeClaim: async (id: string) => {
    await api.post(`/api/v2/provision/claims/${id}/revoke`, undefined, {
      params: tenantParams(),
    });
  },
};

export default accessService;
